import { Ref, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Alert, AlertTitle } from '@/shared/components/ui/alert';
import { Upload, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/shared/components/ui/form';
import { createProfileSchema } from '../schemas/profileSchema';
import { useProfileSubmit } from '../hooks/useProfileSubmit';
import { useImageUpload } from '../hooks/useImageUpload';
import type { OnboardingStep } from '../types/onboarding-types';

interface ProfileStepProps {
  setCurrentStep: (step: OnboardingStep) => void;
  hasSurvey: boolean;
  userFirstName?: string;
  userLastName?: string;
  formRef: Ref<HTMLFormElement>;
  onSubmittingChange?: (isSubmitting: boolean) => void;
  onStepCompleted?: () => void;
  finishAfterProfile?: boolean;
}

export const ProfileStep = ({ setCurrentStep, hasSurvey, userFirstName, userLastName, formRef, onSubmittingChange, onStepCompleted, finishAfterProfile = false }: ProfileStepProps) => {
  const { t } = useTranslation();

  const avatar = useImageUpload();
  const { onSubmit, isSubmitting, error, setError: _setError } = useProfileSubmit(setCurrentStep, hasSurvey, onStepCompleted, finishAfterProfile);
  
  // Notificar mudanças no estado de submitting
  useEffect(() => {
    if (onSubmittingChange) {
      onSubmittingChange(isSubmitting);
    }
  }, [isSubmitting, onSubmittingChange]);
  
  const profileSchema = createProfileSchema(t);
  
  const savedProfileData = typeof window !== 'undefined' 
    ? JSON.parse(sessionStorage.getItem('onboarding_profile') || '{}') 
    : {};

  const profileForm = useForm<z.infer<typeof profileSchema>>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      first_name: savedProfileData.first_name || userFirstName || "",
      last_name: savedProfileData.last_name || userLastName || "",
    },
  });

  useEffect(() => {
    const subscription = profileForm.watch((value) => {
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('onboarding_profile', JSON.stringify(value));
      }
    });
    return () => subscription.unsubscribe();
  }, [profileForm]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedAvatarPreview = sessionStorage.getItem('onboarding_avatar_preview');
      if (savedAvatarPreview) {
        fetch(savedAvatarPreview)
          .then(res => res.blob())
          .then(blob => {
            const file = new File([blob], 'avatar.png', { type: 'image/png' });
            const event = {
              target: {
                files: [file]
              }
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any;
            avatar.handleChange(event);
          })
          .catch(err => {
            console.error('[ProfileStep] Error restoring avatar:', err);
          });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (values: z.infer<typeof profileSchema>) => {
    await onSubmit(values as Parameters<typeof onSubmit>[0], avatar.file);
  };

  return (
    <div className="w-full max-w-md space-y-6 animate-fade-in">
      <div className="flex justify-center">
        <img 
          src="/assets/logo.png" 
          alt="Prodgy Logo" 
          className="h-16 w-auto"
        />
      </div>
      
      {/* Title outside card */}
      <div className="text-center px-4 space-y-2">
        <h1 className="text-2xl font-medium tracking-tight">
          {t("auth.onboarding.profile.title")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t("auth.onboarding.profile.description")}
        </p>
      </div>
      
      {/* Card */}
      <Card className="w-full shadow-lg">
        <CardContent className="px-8 py-8">
          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertTitle>{t("auth.onboarding.errors.title")}</AlertTitle>
            </Alert>
          )}

          <Form {...profileForm}>
            <form ref={formRef} onSubmit={profileForm.handleSubmit(handleSubmit)} className="space-y-6">
              {/* Avatar Upload - Modern Style */}
              <div className="flex flex-col items-center space-y-6 py-6">
                <input
                  ref={avatar.inputRef}
                  type="file"
                  accept="image/*"
                  onChange={avatar.handleChange}
                  className="hidden"
                  disabled={isSubmitting}
                />
                
                <div className="relative">
                  {avatar.preview ? (
                    <div className="relative group">
                      {/* Avatar com overlay hover */}
                      <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-primary/20 shadow-2xl bg-gradient-to-br from-primary/10 to-primary/5 transition-all group-hover:border-primary/30 group-hover:shadow-primary/20">
                        <img
                          src={avatar.preview}
                          alt="Avatar preview"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      
                      {/* Action buttons overlay */}
                      <div className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/40 transition-all duration-200 flex items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                          <Button
                            type="button"
                            variant="secondary"
                            size="icon"
                            className="h-10 w-10 rounded-full shadow-lg bg-white hover:bg-white/90"
                            onClick={() => avatar.inputRef.current?.click()}
                            disabled={isSubmitting}
                          >
                            <Upload className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="destructive"
                            size="icon"
                            className="h-10 w-10 rounded-full shadow-lg"
                            onClick={avatar.handleRemove}
                            disabled={isSubmitting}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => avatar.inputRef.current?.click()}
                      disabled={isSubmitting}
                      className="relative w-32 h-32 rounded-full border-3 border-dashed border-primary/30 bg-gradient-to-br from-primary/5 via-primary/10 to-primary/5 hover:from-primary/10 hover:via-primary/15 hover:to-primary/10 hover:border-primary/50 transition-all duration-300 flex flex-col items-center justify-center group shadow-lg hover:shadow-xl hover:shadow-primary/10"
                    >
                      {/* Icon container */}
                      <div className="w-14 h-14 rounded-full bg-primary/10 group-hover:bg-primary/20 flex items-center justify-center transition-all duration-300 group-hover:scale-110">
                        <Upload className="h-7 w-7 text-primary/60 group-hover:text-primary transition-colors" />
                      </div>
                      
                      {/* Animated ring */}
                      <div className="absolute inset-0 rounded-full border-2 border-primary/0 group-hover:border-primary/20 transition-all duration-300" />
                    </button>
                  )}
                </div>
                
                {/* Helper text */}
                <p className="text-xs text-center text-muted-foreground">
                  Avatar
                </p>
              </div>

              {/* Name fields in badge style */}
              <div className="space-y-4 pt-2">
                <FormField
                  control={profileForm.control}
                  name="first_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium">
                        {t("auth.onboarding.profile.firstNameLabel")} <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input 
                          {...field} 
                          placeholder={t("auth.onboarding.profile.firstNamePlaceholder")}
                          className="h-11"
                          disabled={isSubmitting}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={profileForm.control}
                  name="last_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium">
                        {t("auth.onboarding.profile.lastNameLabel")} <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input 
                          {...field} 
                          placeholder={t("auth.onboarding.profile.lastNamePlaceholder")}
                          className="h-11"
                          disabled={isSubmitting}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
      
      {/* Mensagem de erro geral */}
      <div className={`text-xs text-center -mt-4 h-4 transition-opacity ${
        Object.keys(profileForm.formState.errors).length > 0 
          ? 'text-destructive opacity-100' 
          : 'opacity-0'
      }`}>
        {t('auth.onboarding.validation.requiredFieldsError')}
      </div>
    </div>
  );
};
